import { LiveChecks } from '../checks/progress'
import { useContext, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Issue, Label, Me, Part } from '../api/types'
import { CheckIcon, CommentIcon, DocIcon, PaperclipIcon, PlanIcon, PriorityIcon, UserIcon } from '../components/Icons'
import { useBoardState, useStore } from '../store/context'
import { needsYou, issueProposal } from '../store/store'
import { senderName } from '../sessions/words'
import { PARTS, partText } from '../vocab'
import { ReleaseChips } from '../components/ReleaseChips'
import { releaseSurfaces, SIX, surfaceNamesOf, type SurfaceNames } from '../lib/releases'
import { cx } from '../transcript/bits'
import { BoardInteractionContext } from './interaction'
import { attachmentCount, landedCommitUrl, recordFactsOf } from './cardFacts'
import { liveOf } from './live'
import { LiveLine } from './LiveLine'

// Each marker keeps one colour wherever it shows, set in CSS by its data-marker (Poulpe-741).
const PART_MARKERS: Record<Part, { label: string; Icon: typeof DocIcon }> = {
  description: { label: 'Has a description', Icon: DocIcon },
  plan: { label: 'Has a plan', Icon: PlanIcon },
  outcome: { label: 'Has an outcome', Icon: CheckIcon },
}
const counted = (count: number, one: string, many: string) => count === 1 ? `1 ${one}` : `${count} ${many}`
/** What the card shows beyond the issue: its count of comments and of files attached. */
export interface CardCounts { comments: number; attachments: number }
const NO_COUNTS: CardCounts = { comments: 0, attachments: 0 }
export function CardBody({ issue, labels, me = null, surfaceNames = SIX, counts = NO_COUNTS }: { issue: Issue; labels: ReadonlyMap<string, Label>; me?: Me | null; surfaceNames?: SurfaceNames; counts?: CardCounts }) {
  const claimant = issue.claim ? senderName(issue.claim.by, me) : null
  const own = issue.labels.flatMap(id => {
    const label = labels.get(id)
    return label && label.project === issue.project && !label.deleted_at ? [label] : []
  })
  const filled = PARTS.filter(part => partText(issue, part).trim() !== '')
  // What its landed work touches, a chip per surface, after its labels (Poulpe-500).
  const release = releaseSurfaces(issue, surfaceNames)
  // Each chip carries its label's colour, for the board card's tint (Poulpe-711); the dot and the name keep it without.
  return <>
    <div className="card-top">
      <PriorityIcon priority={issue.priority} /><span className="card-key">{issue.key}</span>
      {claimant && <span className="card-claim" title={`Claimed by ${claimant}`}><UserIcon size={12} />{claimant}</span>}
    </div>
    <div className="card-title">{issue.title}</div>
    {(own.length > 0 || release.length > 0) && <div className="card-labels">{own.map(label => <span key={label.id} className="label-chip" style={{ '--label-colour': label.colour } as CSSProperties}>
      <span className="label-dot" style={{ background: label.colour }} />{label.name}
    </span>)}<ReleaseChips surfaces={release} /></div>}
    {(filled.length > 0 || counts.comments > 0 || counts.attachments > 0) && <div className="card-parts">{filled.map(part => {
      const { label, Icon } = PART_MARKERS[part]
      return <span key={part} data-marker={part} role="img" aria-label={label} title={label}><Icon size={12} /></span>
    })}
      {counts.attachments > 0 && <Marker kind="attachments" label={counted(counts.attachments, 'file attached', 'files attached')} count={counts.attachments}><PaperclipIcon size={12} /></Marker>}
      {counts.comments > 0 && <Marker kind="comments" label={counted(counts.comments, 'comment', 'comments')} count={counts.comments}><CommentIcon size={12} /></Marker>}
    </div>}
  </>
}
function Marker({ kind, label, count, children }: { kind: string; label: string; count: number; children: ReactNode }) {
  return <span data-marker={kind} role="img" aria-label={label} title={label}>{children}<span className="card-marker-count" aria-hidden>{count}</span></span>
}
/** The issue link and Answer are separate links, so both work by keyboard. */
export function Card({ issue }: { issue: Issue }) {
  const board = useContext(BoardInteractionContext)
  const navigate = useNavigate()
  const path = `/${encodeURIComponent(issue.key)}`
  const store = useStore()
  const { labels, questions, pendingAnswers, me, sessions, landings, proposals, returned, projects, entries, attachments } = useBoardState()
  const project = projects.get(issue.project)
  const surfaceNames = surfaceNamesOf(project)
  const facts = recordFactsOf(entries, issue.id)
  const counts = { comments: facts.comments, attachments: attachmentCount(attachments, issue.id) }
  const commit = landedCommitUrl(issue, facts.landed, project?.repository)
  const check = issueProposal(proposals, landings, issue.id)
  const confirmed = store.isIssueConfirmed(issue.id)
  const live = liveOf(issue, sessions, needsYou(questions, me?.member?.id, pendingAnswers), landings, proposals, returned)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: issue.id, disabled: !confirmed })
  return <div ref={setNodeRef} className={cx('card', isDragging && 'is-placeholder', board?.selected === issue.id && 'is-selected')} data-board-card={issue.id} data-priority={issue.priority}
    style={{ transform: CSS.Translate.toString(transform), transition }} aria-busy={!confirmed || undefined}
    {...(confirmed ? attributes : {})} {...listeners}
    onClickCapture={event => {
      if (!board || !confirmed || isDragging || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const target = event.target as HTMLElement
      if (target.closest('button, .card-live-action')) return
      if (target.classList.contains('card-link')) return
      event.preventDefault(); board.select(issue.id); event.currentTarget.focus()
    }}
    onDoubleClick={event => {
      if (!board || !confirmed || isDragging || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
        || (event.target as HTMLElement).closest('button, .card-live-action, input, textarea, select')) return
      event.preventDefault(); board.enlarge(issue.id)
    }}
    onFocus={event => { if (event.target === event.currentTarget) board?.select(issue.id) }}
    onKeyDown={event => {
      if (event.target !== event.currentTarget && !(event.target as HTMLElement).closest('.card-link')) return
      if (isDragging) { listeners?.onKeyDown?.(event); return }
      if (!confirmed) return
      if (event.key === 'Enter') { event.preventDefault(); navigate(path) }
      else if (board && event.key === ' ') { event.preventDefault(); board.enlarge(issue.id) }
      else if (board && event.key.startsWith('Arrow')) { event.preventDefault(); board.step(issue.id, event.key) }
      else listeners?.onKeyDown?.(event)
    }}>
    {confirmed ? <Link className="card-link" to={`/${encodeURIComponent(issue.key)}`}><CardBody issue={issue} labels={labels} me={me} surfaceNames={surfaceNames} counts={counts} /></Link>
      : <CardBody issue={issue} labels={labels} me={me} surfaceNames={surfaceNames} counts={counts} />}
    {check && (check.landing ? <LiveChecks landing={check.landing.id} /> : <LiveChecks proposal={check.proposal.id} />)}
    <LiveLine live={live} issue={issue} confirmed={confirmed} commit={commit && facts.landed ? { url: commit, sha: facts.landed } : null} />
  </div>
}
